import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import RemoveCircleRoundedIcon from '@mui/icons-material/RemoveCircleRounded';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AxiosConfig from '../../../../../../config/AxiosConfig';
import useUsers from '../../../../../hooks/utils/useUsers';
import TaskStatusChip from '../../../../utils/common/TaskStatusChip';
import UserAssignmentAutocomplete from '../../../../utils/common/UserAssignmentAutocomplete';

const Task = ({ task, index, mutateTicket, mutateTask, taskId }) => {
  const navigate = useNavigate();
  const profile = useSelector((state) => state?.login?.profile);
  const [selectedUser, setSelectedUser] = useState(task?.assignedTo || null);
  const { mutate: mutateUsers, isValidating } = useUsers(profile?._id);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedUser?._id !== task?.assignedTo?._id) {
      setIsLoading(true);
      AxiosConfig.create('post', `/tasks/${task?._id}/assignUser`)
        .call({ assignedBy: profile?._id, assignedTo: selectedUser?._id })
        .exit(() => {
          mutateTask();
          mutateTicket();
          mutateUsers();
          setIsLoading(false);
        })
        .catch(console.error);
    }
  }, [selectedUser]);

  const handleDeleteTask = () => {
    AxiosConfig.create('delete', `/tasks/${task?._id}`)
      .call()
      .exit(() => {
        mutateTask();
        mutateTicket();
      })
      .catch(console.error);
  };

  const handleManageTask = () => {
    navigate(
      `/project/${task?.ticket?.project?._id}/tickets/${task?.ticket?._id}/tasks/${task?._id}`
    );
  };

  const getScheduleIconColor = () => {
    if (!task?.scheduledAt) {
      return 'disabled';
    }

    const now = new Date();
    const scheduledDate = new Date(task.scheduledAt);
    const diffInHours = (scheduledDate - now) / (1000 * 60 * 60);

    if (task?.status?.code === 'DONE') {
      return 'success';
    }
    if (diffInHours < 24 && diffInHours >= 0) {
      return 'warning';
    }
    if (diffInHours < 0 && task?.status?.code !== 'DONE') {
      return 'error';
    }
    return 'info';
  };

  return (
    <>
      {index > 0 && <Divider />}
      <Grid
        container
        sx={[
          {
            alignItems: 'center',
            transition:
              'background-color 0.3s ease-in-out, border-left 0.3s ease-in-out',
            backgroundColor:
              task?._id === taskId ? 'rgba(33, 150, 243, 0.1)' : 'inherit',
            borderLeft: task?._id === taskId ? '4px solid #2196F3' : 'none',
          },
        ]}
      >
        {/* Status */}
        <Grid sx={{ textAlign: 'center', minWidth: 90 }} size={1.5}>
          <TaskStatusChip status={task?.status} />
        </Grid>

        {/* Service */}
        <Grid sx={{ minWidth: 80 }} size={1}>
          <Typography align="left">{task?.service?.code}</Typography>
        </Grid>

        {/* Tâche + date planifiée */}
        <Grid sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} size={4}>
          <Typography align="left">{task?.action?.name}</Typography>
          {task?.scheduledAt && (
            <Typography variant="caption" color="text.secondary">
              {new Date(task.scheduledAt).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          )}
        </Grid>

        {/* Assignation */}
        <Grid sx={{ textAlign: 'center', minWidth: 150 }} size={2}>
          {isLoading || isValidating ? (
            <LinearProgress color="info" sx={{ height: 2, width: 150 }} />
          ) : (
            <UserAssignmentAutocomplete
              type="task"
              assignmentId={task?.assignedTo?._id}
              setSelectedUser={setSelectedUser}
              selectedUser={selectedUser}
            />
          )}
        </Grid>

        {/* Dates */}
        <Grid size={1}>
          <Tooltip
            placement="left"
            title={
              <>
                <Typography>{`Création : ${new Date(
                  task?.createdAt
                ).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}`}</Typography>
                {task?.scheduledAt && (
                  <Typography>{`Planifiée : ${new Date(
                    task?.scheduledAt
                  ).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}`}</Typography>
                )}
                {task?.status?.code === 'DONE' && (
                  <Typography>{`Fin : ${new Date(
                    task?.updatedAt
                  ).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}`}</Typography>
                )}
              </>
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EventNoteRoundedIcon
                color={getScheduleIconColor()}
                sx={{ fontSize: 18, marginRight: 1 }}
              />
            </Box>
          </Tooltip>
        </Grid>

        {/* Actions */}
        <Grid sx={{ textAlign: 'center', minWidth: 120 }} size={1.5}>
          <Tooltip title="Supprimer">
            <span>
              <IconButton
                onClick={handleDeleteTask}
                disabled={!!task?.assignedTo}
                sx={{ opacity: task?.assignedTo ? 0.5 : 1 }}
              >
                <RemoveCircleRoundedIcon color="error" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Gérer">
            <span>
              <IconButton
                onClick={handleManageTask}
                disabled={!task?.assignedTo}
                sx={{ opacity: !task?.assignedTo ? 0.5 : 1 }}
              >
                <EditRoundedIcon color="primary" />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
};

const Tasks = ({ tasks, mutateTicket, mutateTask, taskId }) => {
  return tasks?.length > 0 ? (
    <Box sx={{ maxHeight: '70vh', overflow: 'auto', mt: 1 }}>
      <Paper elevation={24}>
        <Grid container sx={{ alignItems: 'center', paddingX: 1 }}>
          <Grid sx={{ textAlign: 'center', minWidth: 90 }} size={1.5}>
            <Typography align="center">Status</Typography>
          </Grid>
          <Grid sx={{ minWidth: 80 }} size={1}>
            <Typography align="left">Service</Typography>
          </Grid>
          <Grid size={4}>
            <Typography align="left">Tâche</Typography>
          </Grid>
          <Grid sx={{ textAlign: 'center', minWidth: 150 }} size={2}>
            <Typography align="center">Assignation</Typography>
          </Grid>
          <Grid sx={{ textAlign: 'center' }} size={1}>
            <Typography align="center">Date</Typography>
          </Grid>
          <Grid sx={{ textAlign: 'center', minWidth: 120 }} size={1.5}>
            <Typography align="center">Actions</Typography>
          </Grid>
        </Grid>

        <Box>
          {tasks.map((task, index) => (
            <Task
              key={task?._id}
              task={task}
              index={index}
              mutateTicket={mutateTicket}
              mutateTask={mutateTask}
              taskId={taskId}
            />
          ))}
        </Box>
      </Paper>
    </Box>
  ) : (
    <Typography variant="h6" align="center" color="text.secondary">
      Aucune tâche créée
    </Typography>
  );
};

export default Tasks;
